import React, { useState, useMemo, useEffect } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import styles from "./ScorePredictionChart.module.css";

/* Custom plugin: for each BAR dataset, draws a connecting line between
 * adjacent bar tops and a circular marker on each bar top. Markers sit
 * dead-center over their bar (matching the target design) instead of at
 * the category center where a regular `line` dataset would place them. */
const BarTopConnectorPlugin = {
  id: "barTopConnector",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      if (!dataset._lineColor) return;

      const meta = chart.getDatasetMeta(datasetIndex);
      if (!meta || meta.hidden) return;

      const points = meta.data
        .map((bar, i) => {
          const v = dataset.data[i];
          if (v === null || v === undefined || Number.isNaN(v)) return null;
          // bar.x is the horizontal center of the bar
          // bar.y is the top edge of the bar
          return { x: bar.x, y: bar.y };
        })
        .filter(Boolean);

      if (points.length === 0) return;

      // Connecting line through the bar tops
      ctx.save();
      ctx.strokeStyle = dataset._lineColor;
      ctx.lineWidth = 2;
      ctx.beginPath();
      points.forEach((p, i) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.stroke();

      // Dot markers — white fill, colored border
      points.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = dataset._lineColor;
        ctx.stroke();
      });

      ctx.restore();
    });
  },
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarTopConnectorPlugin,
);

/* ---------- Helpers ---------- */

const cssVar = (name, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return v || fallback;
};

const COLOR_PAIRS = {
  predicted: {
    darkVar: "--violet-dark",
    lightVar: "--violet-light",
    fallbackDark: "#7C4DFF",
    fallbackLight: "#B197FC",
  },
  actual: {
    darkVar: "--teal-dark",
    lightVar: "--teal-light",
    fallbackDark: "#14B8A6",
    fallbackLight: "#5EEAD4",
  },
};

const getColor = (key) => {
  const p = COLOR_PAIRS[key];
  return {
    dark: cssVar(p.darkVar, p.fallbackDark),
    light: cssVar(p.lightVar, p.fallbackLight),
  };
};

const verticalGradient = (canvasCtx, chartArea, darkStop, lightStop) => {
  const g = canvasCtx.createLinearGradient(
    0,
    chartArea.top,
    0,
    chartArea.bottom,
  );
  g.addColorStop(0, darkStop);
  g.addColorStop(1, lightStop);
  return g;
};

/* =============================================================
 * ScorePredictionChart
 *
 * Loading semantics:
 *   - `loading`      → full-panel "Loading…" (use on initial mount,
 *                      before any tabs exist).
 *   - `chartLoading` → inline "Loading…" inside the chart area;
 *                      keeps tabs visible during tab-switch refetch.
 * ============================================================= */
const ScorePredictionChart = ({
  icon = null,
  title = "Subject-Wise Score Prediction",
  chartTitle = "",
  data = [],
  maxY = 100,
  loading = false,
  chartLoading = false,
  error = null,
  emptyMessage = "No data available",
  predictedLabel = "Predicted Score",
  actualLabel = "Actual Score",
  onSubjectChange,
  initialSubject = null,
}) => {
  const subjects = useMemo(
    () => (Array.isArray(data) ? data.map((d) => d.subject) : []),
    [data],
  );

  const [selectedSubject, setSelectedSubject] = useState(
    initialSubject && subjects.includes(initialSubject)
      ? initialSubject
      : subjects[0] || null,
  );

  // Reset selection only when it disappears from the new subject list.
  // Guard against tab list changing identity but containing the same items.
  useEffect(() => {
    if (subjects.length === 0) {
      if (selectedSubject !== null) setSelectedSubject(null);
      return;
    }
    if (!subjects.includes(selectedSubject)) {
      setSelectedSubject(subjects[0]);
    }
  }, [subjects, selectedSubject]);

  const renderHeader = () => (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        {icon && (
          <span className={styles.icon} aria-hidden="true">
            {icon}
          </span>
        )}
        <h3 className={styles.title}>{title}</h3>
      </div>
    </div>
  );

  /* ---------- Full-panel states (when no tabs exist yet) ---------- */
  if (loading) {
    return (
      <div className="w-100">
        {renderHeader()}
        <div className={styles.stateBox}>Loading…</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="w-100">
        {renderHeader()}
        <div className={`${styles.stateBox} `}>{String(error)}</div>
      </div>
    );
  }
  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="w-100">
        {renderHeader()}
        <div className={styles.stateBox}>{emptyMessage}</div>
      </div>
    );
  }

  /* ---------- Chart data for the selected subject ---------- */
  const subjectData = data.find((d) => d.subject === selectedSubject);
  const exams = subjectData?.exams || [];
  const labels = exams.map((e) => e.exam_name);
  const predictedValues = exams.map(
    (e) => e.predicted ?? e.predicted_score ?? null,
  );
  const actualValues = exams.map((e) => e.actual ?? e.actual_score ?? null);

  const predicted = getColor("predicted");
  const actual = getColor("actual");

  /* Only two bar datasets now. The plugin reads `_lineColor` off each
   * dataset to draw the connecting line + dot markers centered over the
   * bars themselves (not at the category center). */
  const chartData = {
    labels,
    datasets: [
      {
        type: "bar",
        label: predictedLabel,
        data: predictedValues,
        backgroundColor: (ctx) => {
          const { ctx: canvasCtx, chartArea } = ctx.chart;
          if (!chartArea) return predicted.dark;
          return verticalGradient(
            canvasCtx,
            chartArea,
            predicted.dark,
            predicted.light,
          );
        },
        borderColor: predicted.dark,
        borderWidth: 1,
        borderRadius: 8,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
        _lineColor: predicted.dark,
      },
      {
        type: "bar",
        label: actualLabel,
        data: actualValues,
        backgroundColor: (ctx) => {
          const { ctx: canvasCtx, chartArea } = ctx.chart;
          if (!chartArea) return actual.dark;
          return verticalGradient(
            canvasCtx,
            chartArea,
            actual.dark,
            actual.light,
          );
        },
        borderColor: actual.dark,
        borderWidth: 1,
        borderRadius: 8,
        barPercentage: 0.6,
        categoryPercentage: 0.7,
        _lineColor: actual.dark,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
          boxWidth: 14,
          boxHeight: 14,
          padding: 18,
          color: "#374151",
          font: { weight: "500", size: 13 },
        },
      },
      title: { display: !!chartTitle, text: chartTitle, color: "#374151" },
      tooltip: {
        backgroundColor: "#1F2937",
        padding: 12,
        cornerRadius: 8,
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed.y;
            if (val === null || val === undefined) {
              return `${ctx.dataset.label}: —`;
            }
            return `${ctx.dataset.label}: ${val}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: maxY,
        ticks: { stepSize: 10, color: "#6B7280" },
        grid: { color: "#E5E7EB" },
      },
      x: {
        ticks: { color: "#374151", font: { weight: "500" } },
        grid: { display: false },
      },
    },
  };

  const handleSubjectClick = (subj) => {
    if (subj === selectedSubject) return;
    setSelectedSubject(subj);
    if (typeof onSubjectChange === "function") onSubjectChange(subj);
  };

  /* ---------- Inline chart-area state ---------- */
  const renderChartArea = () => {
    if (chartLoading) {
      return <div className={styles.stateBox}>Loading…</div>;
    }
    if (exams.length === 0) {
      return <div className={styles.stateBox}>{emptyMessage}</div>;
    }
    return <Chart type="bar" data={chartData} options={options} />;
  };

  return (
    <div className="w-100">
      {renderHeader()}

      {/* Subject tabs — always visible once data is available */}
      <div className={styles.subjectTabs} role="tablist">
        {subjects.map((subj) => {
          const active = subj === selectedSubject;
          return (
            <button
              key={subj}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => handleSubjectClick(subj)}
              disabled={chartLoading}
              className={`${styles.subjectTab} ${
                active ? styles.subjectTabActive : ""
              }`}
            >
              {subj}
            </button>
          );
        })}
      </div>

      <div className={styles.chartContainer}>{renderChartArea()}</div>
    </div>
  );
};

export default ScorePredictionChart;
